import { FormProvider } from "react-hook-form";
import { nameof } from "ts-simple-nameof";
import { AppDialog } from "../../../../common/components/AppDialog/AppDialog";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { DefaultInputField } from "../../../../common/components/forms/inputs/InputField/DefaultInputField";
import { DefaultFormMultiSelectField } from "../../../../common/components/forms/inputs/dropdown/DefaultFormMultiSelectField";
import { TextEditor } from "../../../../common/components/forms/inputs/textEditor/TextEditor";
import { useMassEmailDialog } from "./hooks/useMassEmailDialog";
import { IMassEmailFormModel } from "./models/massEmailFormModel";
import styles from "./MassEmailDialog.module.scss";

const BODY_TOOLTIP =
  "Можете да използвате следните заместители (placeholder-и) в съдържанието. " +
  "Думите трябва да съвпадат точно:<br/><br/>" +
  "<b>{{userNames}}</b> — пълно име на получателя<br/>" +
  "<b>{{userEmail}}</b> — имейл на получателя<br/>" +
  "<b>{{userPhone}}</b> — телефон на получателя";

interface IMassEmailDialogProps {
  visible: boolean;
  onHide: () => void;
}

export const MassEmailDialog = ({ visible, onHide }: IMassEmailDialogProps) => {
  const {
    form,
    userOptions,
    isUsersLoading,
    isSending,
    isSendingTest,
    testEmailSent,
    selectedUserIds,
    handleSend,
    handleSendTestEmail,
  } = useMassEmailDialog(onHide);

  const selectedCount = selectedUserIds?.length ?? 0;
  const testEmail = form.watch("testEmail");

  return (
    <AppDialog
      visible={visible}
      onHide={onHide}
      header="Изпрати масов имейл"
      style={{ width: "90vw", maxWidth: "780px" }}
    >
      <FormProvider {...form}>
        <form className={styles.form} onSubmit={handleSend}>
          <DefaultFormMultiSelectField
            name={nameof<IMassEmailFormModel>((x) => x.userIds)}
            label="Получатели"
            options={userOptions}
            isLoading={isUsersLoading}
            filter
            filterBy="name"
            includeId={false}
            required
            width="100%"
            emptyMessage="Няма намерени потребители"
            virtualScrollerOptions={{ itemSize: 43 }}
          />

          <DefaultInputField
            name={nameof<IMassEmailFormModel>((x) => x.subject)}
            label="Тема"
            placeholder="Въведете тема на имейла"
            required
            width="100%"
          />

          <TextEditor
            name={nameof<IMassEmailFormModel>((x) => x.body)}
            label="Съдържание"
            tooltip={BODY_TOOLTIP}
            placeholder="Въведете съдържание на имейла"
            required
            width="100%"
          />

          <div className={styles.testEmailRow}>
            <DefaultInputField
              name={nameof<IMassEmailFormModel>((x) => x.testEmail)}
              label="Тестов имейл"
              placeholder="Въведете имейл за тест"
              width="100%"
            />
            <DefaultButton
              label="Изпрати тест"
              design="outline"
              type="button"
              loading={isSendingTest}
              disabled={!testEmail}
              onClick={handleSendTestEmail}
              width="170px"
            />
          </div>

          {testEmailSent && (
            <div className={styles.testEmailSuccess}>
              <i className="pi pi-check-circle" />
              <span>Тестовият имейл беше изпратен успешно.</span>
            </div>
          )}

          {selectedCount > 0 && (
            <div className={styles.disclaimer}>
              <span>
                {selectedCount === 1 ? (
                  <>Внимание: ще бъде изпратен <strong>1</strong> имейл.</>
                ) : (
                  <>Внимание: ще бъдат изпратени <strong>{selectedCount}</strong> имейла.</>
                )}
              </span>
            </div>
          )}

          <div className={styles.footer}>
            <DefaultButton
              label="Отказ"
              design="outline"
              onClick={onHide}
              type="button"
              width="auto"
            />
            <DefaultButton
              label="Изпрати"
              design="default"
              type="submit"
              loading={isSending}
              disabled={selectedCount === 0}
              width="auto"
            />
          </div>
        </form>
      </FormProvider>
    </AppDialog>
  );
};
